import React, { useState, useContext } from "react";
import { UserContext } from "../context/UserContext";

// Constants for dropdowns
const STATE_OPTIONS = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"];
const CHAMBER_OPTIONS = ["House of Representatives", "Senate"];
const STATUS_OPTIONS = ["active", "inactive"];

const AddPoliticians = ({ onClose, politician = null }) => {
  const { emailId } = useContext(UserContext);
  
  // Form states
  const [name, setName] = useState(politician?.name || "");
  const [constituency, setConstituency] = useState(politician?.constituency || "");
  const [party, setParty] = useState(politician?.party || "");
  const [chamber, setChamber] = useState(politician?.chamber || CHAMBER_OPTIONS[0]);
  const [description, setDescription] = useState(politician?.description || "");
  const [position, setPosition] = useState(politician?.position || "");
  const [state, setState] = useState(politician?.state || STATE_OPTIONS[0]);
  const [status, setStatus] = useState(politician?.status || STATUS_OPTIONS[0]);
  const [targetAmount, setTargetAmount] = useState(politician?.target_amount || 0);
  
  // Media states
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [mediaUrl, setMediaUrl] = useState(politician?.image_url || "");
  
  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Add new states for banking details
  const [accountName, setAccountName] = useState(politician?.account_name || "");
  const [accountNumber, setAccountNumber] = useState(politician?.account_number || "");
  const [bsbNumber, setBsbNumber] = useState(politician?.bsb || "");
  // Add new states for new columns
  const [profileUrl, setProfileUrl] = useState(politician?.profile_url || "");
  const [parliamentaryService, setParliamentaryService] = useState(politician?.parliamentary_service || "");
  const [isCandidate, setIsCandidate] = useState(politician?.is_candidate === "1" ? "1" : "");
  const [isDisplayed, setIsDisplayed] = useState(
    typeof politician?.is_displayed !== "undefined" ? String(politician.is_displayed) : "1"
  );

  // File validation
  const validateFile = (file) => {
    const allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const allowedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
    const maxSize = 500 * 1024 * 1024; // 500MB

    if (![...allowedImageTypes, ...allowedVideoTypes].includes(file.type)) {
      alert('Invalid file type. Only images (JPG, PNG, GIF) and videos (MP4, MOV, AVI) are allowed.');
      return false;
    }

    if (file.size > maxSize) {
      alert('File is too large. Maximum size is 500MB.');
      return false;
    }

    return true;
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && validateFile(file)) {
      setMediaFile(file);
      setMediaType(file.type);
    }
  };

  // Upload media to S3
  const uploadMediaToS3 = async (file) => {
    const formData = new FormData();
    formData.append("media", file);

    try {
      const response = await fetch("/api/upload-s3", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Media upload failed");

      const data = await response.json();
      return { url: data.mediaUrl, type: data.mediaType };
    } catch (error) {
      console.error("Error uploading media:", error);
      throw error;
    }
  };

  // Add validation handlers for BSB and Account numbers
  const handleBsbNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) { // Limit to 6 digits
      setBsbNumber(value);
    }
  };

  const handleAccountNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 10) { // Limit to 10 digits
      setAccountNumber(value);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !constituency || !party || !chamber || !description || !position || !state || (!politician && !mediaFile)) {
      alert("Please fill in all required fields!");
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      let mediaData = { url: mediaUrl, type: mediaType };

      setUploadProgress(30);

      if (mediaFile) {
        setUploadProgress(50);
        mediaData = await uploadMediaToS3(mediaFile);
      }

      setUploadProgress(70);

      const formData = {
        name,
        constituency,
        party,
        chamber,
        description,
        image_url: mediaData.url,
        position,
        state,
        status,
        target_amount: parseFloat(targetAmount) || 0,
        account_name: accountName,
        account_number: accountNumber,
        bsb: bsbNumber,
        created_by: emailId,
        profile_url: profileUrl,
        parliamentary_service: parliamentaryService,
        is_candidate: isCandidate === "1" ? "1" : null,
        is_displayed: Number(isDisplayed),
      };

      setUploadProgress(90);

      const url = politician
        ? `/api/politicians/${politician.id}`
        : "/api/politicians";
      
      const response = await fetch(url, {
        method: politician ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${politician ? "update" : "create"} politician`);
      }

      setUploadProgress(100);
      alert(`✅ Politician ${politician ? "updated" : "added"} successfully!`);
      onClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setTimeout(() => {
        setIsSubmitting(false);
        setUploadProgress(0);
      }, 1000);
    }
  };

  return (
    <div className="popup-section" style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999,
    }}>
      <div className="popup-container" style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "900px",
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
        position: "relative",
      }}>
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          {politician ? "Edit Politician" : "Add New Politician"}
        </h3>

        <button onClick={onClose} style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          background: "none",
          border: "none",
          fontSize: "1.5rem",
          cursor: "pointer",
        }}>&times;</button>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              required
            />
          </div>

          {/* Constituency */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Constituency *</label>
            <input
              type="text"
              value={constituency}
              onChange={(e) => setConstituency(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              required
            />
          </div>

          {/* Party */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Party *</label>
            <input
              type="text"
              value={party}
              onChange={(e) => setParty(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              required
            />
          </div>

          {/* Chamber */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Chamber *</label>
            <select
              value={chamber}
              onChange={(e) => setChamber(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              required
            >
              {CHAMBER_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Position */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Position *</label>
            <input
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              required
            />
          </div>

          {/* State */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>State *</label>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              required
            >
              {STATE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
                minHeight: "100px",
              }}
              required
            />
          </div>

          {/* Media Upload */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>
              {politician ? "Update Image" : "Upload Image *"}
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept="image/*,video/*"
              style={{ width: "100%" }}
              required={!politician}
            />
            {mediaUrl && (
              <img
                src={mediaUrl}
                alt="Preview"
                style={{
                  marginTop: "10px",
                  maxWidth: "200px",
                  maxHeight: "200px",
                  objectFit: "cover",
                }}
              />
            )}
          </div>

          {/* Status */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          {/* Target Amount */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Target Amount</label>
            <input
              type="number"
              value={targetAmount}
              onChange={(e) => setTargetAmount(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              min="0"
              step="0.01"
            />
          </div>

          {/* Add banking fields before the submit button */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Account Name</label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Account Number (9-10 digits)</label>
            <input
              type="text"
              value={accountNumber}
              onChange={handleAccountNumberChange}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              minLength="9"
              maxLength="10"
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>BSB Number (6 digits)</label>
            <input
              type="text"
              value={bsbNumber}
              onChange={handleBsbNumberChange}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
              minLength="6"
              maxLength="6"
            />
          </div>

          {/* Profile URL */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Profile URL</label>
            <input
              type="text"
              value={profileUrl}
              onChange={(e) => setProfileUrl(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          {/* Parliamentary Service */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Parliamentary Service</label>
            <input
              type="text"
              value={parliamentaryService}
              onChange={(e) => setParliamentaryService(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            />
          </div>

          {/* Is Displayed */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Display to Normal Users?</label>
            <select
              value={isDisplayed}
              onChange={e => setIsDisplayed(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <option value="1">Yes</option>
              <option value="0">No (Admin Only)</option>
            </select>
          </div>

          {/* Is Candidate */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Is Candidate?</label>
            <select
              value={isCandidate}
              onChange={e => setIsCandidate(e.target.value)}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ddd",
              }}
            >
              <option value="">No</option>
              <option value="1">Yes</option>
            </select>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              opacity: isSubmitting ? 0.7 : 1,
            }}
          >
            {isSubmitting ? "Submitting..." : politician ? "Update Politician" : "Add Politician"}
          </button>

          {/* Progress Bar */}
          {isSubmitting && (
            <div style={{
              marginTop: "15px",
              backgroundColor: "#f0f0f0",
              borderRadius: "4px",
              overflow: "hidden",
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: "20px",
                backgroundColor: "#007bff",
                transition: "width 0.3s ease-in-out",
              }} />
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AddPoliticians;